import { CALL_CENTER_IAM_WRITE_FLAGS } from '@/shared/constants/call-center-iam';
/** Normalize IAM permission entries (string or `{ action }`) into action strings. */
export function collectPermissionActions(value) {
    if (!Array.isArray(value))
        return [];
    return value
        .map((entry) => {
        if (typeof entry === 'string')
            return entry.trim();
        if (entry && typeof entry === 'object') {
            const row = entry;
            return String(row.action ?? row.name ?? '').trim();
        }
        return '';
    })
        .filter(Boolean);
}
/** Flatten nested `roles[].permissions[].action` from IAM user/account payloads. */
export function flattenPermissionActionsFromRoles(roles) {
    if (!Array.isArray(roles))
        return [];
    const actions = new Set();
    for (const role of roles) {
        if (role && typeof role === 'object' && 'permissions' in role) {
            for (const action of collectPermissionActions(role.permissions)) {
                actions.add(action);
            }
        }
    }
    return [...actions];
}
function resolveRoleName(role) {
    return ('roleName' in role ? role.roleName : undefined) || role.name || '';
}
function findRoleInCatalog(role, catalog) {
    return catalog.find((entry) => (entry.id != null && role.id != null && String(entry.id) === String(role.id)) ||
        resolveRoleName(entry) === resolveRoleName(role));
}
/** IAM user create/update expects permission action strings, not role names (see monolithic user-management). */
export function collectAuthoritiesFromSelectedRoles(selectedRoles, catalog = []) {
    const actions = new Set();
    for (const selected of selectedRoles) {
        const catalogRole = findRoleInCatalog(selected, catalog);
        const permissions = catalogRole?.permissions ??
            ('permissions' in selected && Array.isArray(selected.permissions) ? selected.permissions : []);
        for (const action of collectPermissionActions(permissions)) {
            actions.add(action);
        }
    }
    return [...actions];
}
function resolveSelectedRolesForWrite(selectedRoles, catalog = []) {
    return selectedRoles.map((role) => {
        if (isUserApiRole(role)) {
            const catalogRole = findRoleInCatalog(role, catalog);
            if (catalogRole)
                return catalogRole;
            return {
                id: role.id,
                name: role.name,
                roleName: role.name,
                description: role.description,
                active: role.active !== false,
                permissions: role.permissions,
            };
        }
        const uiRole = role;
        return findRoleInCatalog(uiRole, catalog) ?? uiRole;
    });
}
function toUserApiPermission(perm) {
    const action = String(perm.action ?? '').trim();
    return {
        ...(perm.id != null && perm.id !== '' ? { id: perm.id } : {}),
        action,
        description: perm.description ?? null,
        active: perm.active !== false,
        roles: [],
    };
}
/** Map a loaded IAM/UI role → nested role on user create/update body. */
export function toUserApiRolePayload(role) {
    const name = String(role.roleName ?? role.name ?? '').trim();
    let permissions = (role.permissions ?? [])
        .map(toUserApiPermission)
        .filter((p) => Boolean(p.action) || p.id != null);
    // IAM user create expects nested permissions (e.g. ROLE_ADMIN on the role object).
    if (permissions.length === 0 && name) {
        permissions = [{ action: name, description: null, active: true, roles: [] }];
    }
    return {
        ...(role.id != null && role.id !== '' ? { id: role.id } : {}),
        name,
        description: role.description ?? '',
        active: role.active !== false,
        permissions,
        systemUsers: [],
    };
}
function isUserApiRole(value) {
    return typeof value === 'object' && value !== null && 'name' in value;
}
/** Map IAM `GET /admin/users/{id}` response → user create/update form values. */
export function iamAdminUserToFormValues(dto) {
    const roles = (dto.roles ?? []).map((role) => {
        const nestedPermissions = (role.permissions ?? []).map((p) => ({
            id: p.id,
            action: p.action,
            description: p.description ?? null,
            active: p.active !== false,
        }));
        const permissions = nestedPermissions.length > 0
            ? nestedPermissions
            : role.name
                ? [{ action: role.name, description: null, active: true }]
                : [];
        return {
            id: role.id,
            name: role.name,
            description: role.description ?? '',
            active: role.active !== false,
            permissions,
            systemUsers: role.systemUsers ?? [],
        };
    });
    const flatFromDto = collectPermissionActions(dto.permissions);
    const authorities = flatFromDto.length > 0 ? flatFromDto : flattenPermissionActionsFromRoles(roles);
    const branches = Array.isArray(dto.branchIds)
        ? dto.branchIds
        : dto.defaultBranchId != null
            ? [dto.defaultBranchId]
            : [];
    return {
        id: dto.id,
        login: dto.login,
        email: dto.email,
        firstName: dto.firstName,
        lastName: dto.lastName,
        activated: dto.activated,
        authorities,
        roles,
        branches,
    };
}
/** Build the CRM user write body (create/update). */
export function buildUserWritePayload(data, existing, roleCatalog, 
/** PickList selection — preferred over `data.roles` (mirrors finlevia `targetRoles`). */
selectedRolesOverride) {
    const roleInput = selectedRolesOverride != null && selectedRolesOverride.length > 0
        ? [...selectedRolesOverride]
        : Array.isArray(data.roles)
            ? data.roles
            : [];
    const selectedRoles = resolveSelectedRolesForWrite(roleInput, roleCatalog);
    const roles = selectedRoles.map((role) => toUserApiRolePayload(role));
    const flatPermissions = collectAuthoritiesFromSelectedRoles(selectedRoles, roleCatalog);
    const permissions = flatPermissions.length > 0
        ? flatPermissions
        : Array.isArray(data.authorities) && data.authorities.length > 0
            ? data.authorities.map(String)
            : [];
    return {
        ...(existing?.id != null ? { id: existing.id } : {}),
        login: data.login?.trim(),
        firstName: data.firstName?.trim(),
        lastName: data.lastName?.trim(),
        email: data.email?.trim(),
        activated: data.activated ?? true,
        permissions,
        roles,
        branches: Array.isArray(data.branches) ? data.branches : existing?.branches ?? [],
        isCallCenterUser: CALL_CENTER_IAM_WRITE_FLAGS.isCallCenterUser,
    };
}
